/**
 * ExportButtons.tsx
 * Component for exporting audit results and action items to PDF/CSV
 */

import React, { useState } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Snackbar,
  Alert,
  Tooltip,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import DownloadIcon from '@mui/icons-material/Download';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import api from '../services/api';
import LoadingModal from './LoadingModal';

interface ExportButtonsProps {
  /** Audit session ID for exporting audit results */
  sessionId?: number;
  /** Company ID for exporting all action items */
  companyId?: number;
  /** Variant of the button display */
  variant?: 'button' | 'menu';
  /** Size of the button */
  size?: 'small' | 'medium' | 'large';
  /** Whether to show action items export option */
  showActionItems?: boolean;
}

const ExportButtons: React.FC<ExportButtonsProps> = ({
  sessionId,
  companyId,
  variant = 'button',
  size = 'medium',
  showActionItems = true,
}) => {
  const [isExporting, setIsExporting] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const open = Boolean(anchorEl);

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const downloadFile = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleExportPDF = async () => {
    if (!sessionId) return;
    
    handleMenuClose();
    setIsExporting('pdf');
    
    try {
      const response = await api.get(`/audit/sessions/${sessionId}/export/pdf/`, {
        responseType: 'blob',
      });
      
      downloadFile(response.data, `audit_report_${sessionId}.pdf`);
      setSnackbar({
        open: true,
        message: 'PDF report downloaded successfully!',
        severity: 'success',
      });
    } catch (error: any) {
      console.error('PDF export error:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to generate PDF report',
        severity: 'error',
      });
    } finally {
      setIsExporting(null);
    }
  };

  const handleExportAuditCSV = async () => {
    if (!sessionId) return;
    
    handleMenuClose();
    setIsExporting('audit-csv');
    
    try {
      const response = await api.get(`/audit/sessions/${sessionId}/export/csv/`, {
        responseType: 'blob',
      });
      
      downloadFile(response.data, `audit_results_${sessionId}.csv`);
      setSnackbar({
        open: true,
        message: 'Review results CSV downloaded successfully!',
        severity: 'success',
      });
    } catch (error: any) {
      console.error('CSV export error:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to export CSV',
        severity: 'error',
      });
    } finally {
      setIsExporting(null);
    }
  };

  const handleExportActionItemsCSV = async () => {
    handleMenuClose();
    setIsExporting('action-csv');
    
    try {
      const params = new URLSearchParams();
      if (sessionId) {
        params.append('session_id', sessionId.toString());
      } else if (companyId) {
        params.append('company_id', companyId.toString());
      }
      
      const response = await api.get(`/audit/action-items/export/csv/?${params.toString()}`, {
        responseType: 'blob',
      });
      
      const filename = sessionId 
        ? `action_items_audit_${sessionId}.csv`
        : `action_items_company_${companyId}.csv`;
      
      downloadFile(response.data, filename);
      setSnackbar({
        open: true,
        message: 'Action items CSV downloaded successfully!',
        severity: 'success',
      });
    } catch (error: any) {
      console.error('Action items CSV export error:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to export action items',
        severity: 'error',
      });
    } finally {
      setIsExporting(null);
    }
  };

  // Get export type display messages
  const getExportMessage = () => {
    switch (isExporting) {
      case 'pdf':
        return { title: 'Generating PDF Report', message: 'Creating a comprehensive PDF report of your audit results.' };
      case 'audit-csv':
        return { title: 'Exporting to CSV', message: 'Preparing your audit results data for download.' };
      case 'action-csv':
        return { title: 'Exporting Action Items', message: 'Preparing your action items for download.' };
      default:
        return { title: 'Exporting', message: 'Preparing your data for download.' };
    }
  };
  
  const exportMessage = getExportMessage();

  // Menu variant - dropdown with all options
  if (variant === 'menu') {
    return (
      <>
        <LoadingModal
          open={!!isExporting}
          title={exportMessage.title}
          message={exportMessage.message}
        />
        
        <Button
          variant="outlined"
          size={size}
          onClick={handleMenuClick}
          endIcon={<KeyboardArrowDownIcon />}
          startIcon={isExporting ? <CircularProgress size={16} /> : <DownloadIcon />}
          disabled={!!isExporting}
        >
          Export
        </Button>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          {sessionId && (
            <MenuItem onClick={handleExportPDF} disabled={isExporting === 'pdf'}>
              <ListItemIcon>
                {isExporting === 'pdf' ? (
                  <CircularProgress size={20} />
                ) : (
                  <PictureAsPdfIcon fontSize="small" color="error" />
                )}
              </ListItemIcon>
              <ListItemText>Export PDF Report</ListItemText>
            </MenuItem>
          )}
          {sessionId && (
            <MenuItem onClick={handleExportAuditCSV} disabled={isExporting === 'audit-csv'}>
              <ListItemIcon>
                {isExporting === 'audit-csv' ? (
                  <CircularProgress size={20} />
                ) : (
                  <TableChartIcon fontSize="small" color="success" />
                )}
              </ListItemIcon>
              <ListItemText>Export Audit Results (CSV)</ListItemText>
            </MenuItem>
          )}
          {showActionItems && (sessionId || companyId) && (
            <MenuItem onClick={handleExportActionItemsCSV} disabled={isExporting === 'action-csv'}>
              <ListItemIcon>
                {isExporting === 'action-csv' ? (
                  <CircularProgress size={20} />
                ) : (
                  <TableChartIcon fontSize="small" color="primary" />
                )}
              </ListItemIcon>
              <ListItemText>Export Action Items (CSV)</ListItemText>
            </MenuItem>
          )}
        </Menu>
        
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert 
            onClose={() => setSnackbar({ ...snackbar, open: false })} 
            severity={snackbar.severity}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </>
    );
  }

  // Button variant - individual buttons
  return (
    <>
      <LoadingModal
        open={!!isExporting}
        title={exportMessage.title}
        message={exportMessage.message}
      />
      
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {sessionId && (
          <Tooltip title="Download PDF Report">
            <Button
              variant="outlined"
              size={size}
              onClick={handleExportPDF}
              startIcon={isExporting === 'pdf' ? <CircularProgress size={16} /> : <PictureAsPdfIcon />}
              disabled={!!isExporting}
              color="error"
            >
              PDF
            </Button>
          </Tooltip>
        )}
        {sessionId && (
          <Tooltip title="Download Audit Results as CSV">
            <Button
              variant="outlined"
              size={size}
              onClick={handleExportAuditCSV}
              startIcon={isExporting === 'audit-csv' ? <CircularProgress size={16} /> : <TableChartIcon />}
              disabled={!!isExporting}
              color="success"
            >
              CSV
            </Button>
          </Tooltip>
        )}
        {showActionItems && (sessionId || companyId) && (
          <Tooltip title="Download Action Items as CSV">
            <Button
              variant="outlined"
              size={size}
              onClick={handleExportActionItemsCSV}
              startIcon={isExporting === 'action-csv' ? <CircularProgress size={16} /> : <TableChartIcon />}
              disabled={!!isExporting}
              color="primary"
            >
              Action Items
            </Button>
          </Tooltip>
        )}
      </Box>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ExportButtons;

